import { useMemo } from 'react';
import { Alert, Box, Chip, Typography } from '@mui/material';
import { integrationsHash } from '@constants/integration';
import { IntegrationState } from 'types';
import { GalaxyService } from 'TenantModule/api/getServicesApi';
import { useServicesQuery } from 'TenantModule/api/queries/useServicesQuery';
import { TeamSearchIntegration, TeamSearchItem } from 'TeamModule/api/searchTeamsByFieldsApi';

/**
 * The agent data this migration would carry for the source team.
 *
 * Shown from the team search result the picker already holds, so it appears the moment the
 * source team is chosen rather than waiting for the pre-flight.
 *
 * It is still galaxy's CACHED view of integration-server, refreshed on a cron, so the
 * pre-flight's service plan remains the authority — this answers "have I picked the right
 * team", and says so.
 */

/**
 * `state` is the contractual source of truth (GLX-1602). `archivedAt` is a fallback only
 * for rows that predate the field — the same rule as the repo's `isIntegrationArchived`.
 */
const isArchived = (integration: TeamSearchIntegration) =>
  integration.state ? integration.state === IntegrationState.ARCHIVED : !!integration.archivedAt;

const labelFor = (targetService: string) => integrationsHash[targetService]?.label ?? targetService;

/** Legacy rows stored a bare service name instead of an object. */
const normalize = (entry: string | TeamSearchIntegration): TeamSearchIntegration =>
  typeof entry === 'string'
    ? { targetService: entry, targetServiceId: 0, state: '', archivedAt: null }
    : entry;

/**
 * The galaxy service an integration maps to, or undefined when nothing matches.
 *
 * Mirrors galaxy's `reverseTranslateServiceName`: match the integration-server name
 * against a non-archived service's cloud or on-prem name and take its `serviceName`.
 * Duplicated here so the card can answer before a pre-flight; the pre-flight is what
 * actually decides, so a drift between the two shows up as this list disagreeing with the
 * plan rather than as a wrong migration.
 */
const serviceFor = (targetService: string, services: GalaxyService[]) =>
  services.find(
    (service) =>
      !service.isArchived &&
      (service.cloudIntegrationName === targetService ||
        service.onPremIntegrationName === targetService),
  );

/**
 * Whether an integration's data actually moves.
 *
 * Galaxy keys on the resolved service NAME containing `-agent`, not on the catalog's
 * `isIntegrationAgent` flag, so this matches on the name for the same reason.
 */
const migratesAsAgent = (targetService: string, services: GalaxyService[]) =>
  !!serviceFor(targetService, services)?.serviceName.includes('-agent');

export const TeamIntegrationsSummary = ({
  team,
  migrateArchivedIntegrations,
}: {
  team: TeamSearchItem | null;
  migrateArchivedIntegrations: boolean;
}) => {
  const { services, isLoading: servicesLoading } = useServicesQuery({ enabled: !!team });

  const { carried, skippedArchived, notAgentBacked, unresolved } = useMemo(() => {
    const entries = (team?.integrations ?? []).map(normalize);

    // A team's integration history is unbounded and repeats a service across states.
    // Dedupe on the service so the operator sees systems, not rows, preferring a live row.
    const seen = new Map<string, TeamSearchIntegration>();
    entries.forEach((entry) => {
      const existing = seen.get(entry.targetService);
      if (!existing || (isArchived(existing) && !isArchived(entry))) {
        seen.set(entry.targetService, entry);
      }
    });

    const unique = [...seen.values()];
    const inScope = migrateArchivedIntegrations ? unique : unique.filter((e) => !isArchived(e));

    // Three distinct verdicts, kept apart because they mean different things to an
    // operator comparing this panel against the pre-flight: resolved-and-agent-backed,
    // resolved-and-not, and not resolvable at all. Lumping the last two together reads as
    // a settled "will not migrate" for something this page simply could not identify.
    const resolves = (e: TeamSearchIntegration) => !!serviceFor(e.targetService, services);

    return {
      carried: inScope.filter((e) => migratesAsAgent(e.targetService, services)),
      unresolved: inScope.filter((e) => !resolves(e)),
      // Agent-backed only: ticking the box moves an archived entry into scope, but a
      // non-agent one still would not migrate, so promising it would be a lie.
      // Empty when the toggle is on — nothing is then skipped for being archived.
      skippedArchived: migrateArchivedIntegrations
        ? []
        : unique.filter((e) => isArchived(e) && migratesAsAgent(e.targetService, services)),
      notAgentBacked: inScope.filter(
        (e) => resolves(e) && !migratesAsAgent(e.targetService, services),
      ),
    };
  }, [team, migrateArchivedIntegrations, services]);

  if (!team) return null;

  // null means galaxy has never indexed this team's integrations, which is not the same as
  // the team having none — saying "no integrations" here would be a guess.
  if (team.integrations == null) {
    return (
      <Alert severity="info">
        Galaxy has no integration record for this team yet, so its agent data cannot be listed here.
        Run the pre-flight to get the real service plan.
      </Alert>
    );
  }

  // Without the catalog the agent mapping cannot be resolved, and an empty list would read
  // as "nothing to migrate" rather than "not known yet".
  if (servicesLoading || services.length === 0) {
    return (
      <Alert severity="info">
        {servicesLoading
          ? 'Working out which of this team’s integrations carry agent data…'
          : 'The service catalog could not be loaded, so the agent list cannot be resolved here. The pre-flight returns the real service plan.'}
      </Alert>
    );
  }

  return (
    <Alert severity="info">
      <Typography variant="body2" gutterBottom>
        {carried.length > 0
          ? `Agent data for ${carried.length} integration${carried.length === 1 ? '' : 's'} would be migrated:`
          : unresolved.length > 0
            ? // Not "none": there are entries this page could not identify, so a flat zero
              // would be a claim it has no basis for.
              'No agent data was identified for this team, but some of its integrations could not be matched — see below.'
            : 'No integration agent data would be migrated for this team.'}
      </Typography>

      {carried.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          {carried.map((entry) => (
            <Chip
              key={`${entry.targetService}-${entry.targetServiceId}`}
              size="small"
              label={labelFor(entry.targetService)}
              color={isArchived(entry) ? 'default' : 'primary'}
              variant={isArchived(entry) ? 'outlined' : 'filled'}
            />
          ))}
        </Box>
      )}

      {notAgentBacked.length > 0 && (
        <Typography variant="caption" display="block" gutterBottom>
          {notAgentBacked.length} integration{notAgentBacked.length === 1 ? '' : 's'} carry no agent
          data of their own and are not migrated separately:{' '}
          {notAgentBacked.map((entry) => labelFor(entry.targetService)).join(', ')}.
        </Typography>
      )}

      {unresolved.length > 0 && (
        <Typography variant="caption" display="block" gutterBottom>
          {/* Not the same verdict: these match no service in the catalog, so this page
              cannot say either way and should not imply it has decided. */}
          {unresolved.length} integration{unresolved.length === 1 ? '' : 's'} could not be matched
          to a known service, so whether {unresolved.length === 1 ? 'it carries' : 'they carry'}{' '}
          agent data is not known here:{' '}
          {unresolved.map((entry) => labelFor(entry.targetService)).join(', ')}. The pre-flight
          resolves {unresolved.length === 1 ? 'it' : 'them'} properly.
        </Typography>
      )}

      {skippedArchived.length > 0 && (
        <Typography variant="caption" display="block" gutterBottom>
          {skippedArchived.length} archived integration
          {skippedArchived.length === 1 ? '' : 's'} would be skipped:{' '}
          {skippedArchived.map((entry) => labelFor(entry.targetService)).join(', ')}. Tick
          &ldquo;include archived integrations&rdquo; to carry them too.
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" display="block">
        rails-api, api-server and integration-server always migrate. This is galaxy&apos;s recorded
        view of the team, so treat it as a check that you picked the right one — the pre-flight
        below returns the actual service plan.
      </Typography>
    </Alert>
  );
};

export default TeamIntegrationsSummary;
